import { Navigate } from "react-router-dom";
import { useOrgRoleSurfaces } from "@/hooks/useOrgRoleSurfaces";
import { Skeleton } from "@/components/ui/skeleton";
import RoleGuard from "@/components/auth/RoleGuard";
import Inventory from "./Inventory";

/**
 * BLU-666 — component switch for `/`.
 *
 * A pure 3PL client's home is their order inbox, not the stock dashboard:
 * watching fulfilment is the job, holding stock is incidental. So `/` sends
 * them to `/orders` — which makes it their landing page from login, the logo,
 * and a bookmarked root alike. The dashboard stays reachable for them at
 * `/inventory` (their sidebar's Inventory item points there).
 *
 * Owner / hybrid orgs are untouched: `/` is still the Inventory dashboard.
 *
 * `isPureClient` stays false while `getOrgRoleSurfaces` is loading, disabled
 * (shared-locations flag off) or erroring — the established BLU-658 contract
 * (never redirect on an unconfirmed answer). The only ambiguous window is
 * `isLoading`, where we render a neutral skeleton rather than flash the
 * dashboard and then bounce. Mirrors OrdersRouteSwitch.
 */
export default function HomeRouteSwitch() {
  const { isPureClient, isLoading } = useOrgRoleSurfaces();

  if (isPureClient) {
    return <Navigate to="/orders" replace />;
  }

  if (isLoading) {
    return <HomeRouteSkeleton />;
  }

  return (
    <RoleGuard
      allowedRoles={[
        "Super Admin",
        "Admin",
        "Warehouse Coordinator",
        "Warehouse Staff",
        "Viewer",
        "CX",
      ]}
    >
      <Inventory />
    </RoleGuard>
  );
}

/** Shown only during the brief `getOrgRoleSurfaces` resolving window —
 *  deliberately generic so it resembles neither surface. */
function HomeRouteSkeleton() {
  return (
    <div className="flex flex-col">
      <main className="w-full flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 pb-2 pt-3">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-8 w-40" />
        </div>
        <div className="mt-6 space-y-2 rounded-lg border bg-card p-4 shadow">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      </main>
    </div>
  );
}
